import {
	Check,
	ChevronLeft,
	ChevronRight,
	Search,
	Send,
	Store,
	Users,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SERVER_URL } from "../api/client";
import { PageHeader } from "../components/layout/PageHeader";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface CustomerListItem {
	id: number;
	email: string;
	name: string;
	phone: string | null;
	role: string;
	verified: boolean;
	createdAt: string;
	merchantId: number | null;
	merchantName: string | null;
	merchantWebsite: string | null;
}

interface CustomersResponse {
	items: CustomerListItem[];
	total: number;
	page: number;
	totalPages: number;
}

type ResendState = "idle" | "sending" | "sent" | "error";

// ---------------------------------------------------------------------------
// Customers page (merchant_users)
// ---------------------------------------------------------------------------
const Customers = () => {
	const [customers, setCustomers] = useState<CustomerListItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [verifiedFilter, setVerifiedFilter] = useState<string>("");
	const [resend, setResend] = useState<Record<number, ResendState>>({});

	const fetchCustomers = useCallback(async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams({ page: String(page) });
			if (search) params.set("q", search);
			if (verifiedFilter) params.set("verified", verifiedFilter);

			const res = await fetch(`${SERVER_URL}/merchant-users?${params}`, {
				credentials: "include",
			});
			if (res.ok) {
				const d = (await res.json()) as CustomersResponse;
				setCustomers(d.items);
				setTotalPages(d.totalPages);
				setTotal(d.total);
			}
		} catch {
			// ignore
		} finally {
			setLoading(false);
		}
	}, [page, search, verifiedFilter]);

	useEffect(() => {
		fetchCustomers();
	}, [fetchCustomers]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(1);
		setSearch(searchInput);
	};

	const handleResend = async (id: number) => {
		setResend((prev) => ({ ...prev, [id]: "sending" }));
		try {
			const res = await fetch(
				`${SERVER_URL}/merchant-users/${id}/resend-welcome`,
				{ method: "POST", credentials: "include" },
			);
			setResend((prev) => ({ ...prev, [id]: res.ok ? "sent" : "error" }));
		} catch {
			setResend((prev) => ({ ...prev, [id]: "error" }));
		}
	};

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Shop · Customers"
				title={`${total.toLocaleString("en-US")} customers.`}
				qualifier="The people behind the shops."
				description="Merchant users — browse and resend the welcome email."
			/>

			{/* Search bar + filters */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<form onSubmit={handleSearch} className="relative flex-1 max-w-xl">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
					<input
						type="text"
						placeholder="Search by name, email, merchant…"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10 transition-all"
					/>
					{search && (
						<button
							type="button"
							onClick={() => {
								setSearch("");
								setSearchInput("");
								setPage(1);
							}}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
						>
							<X className="w-4 h-4" />
						</button>
					)}
				</form>

				<div className="flex items-center gap-2 flex-wrap">
					<select
						value={verifiedFilter}
						onChange={(e) => {
							setVerifiedFilter(e.target.value);
							setPage(1);
						}}
						className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 bg-white text-slate-600 cursor-pointer outline-none focus:border-primary/40"
					>
						<option value="">All Status</option>
						<option value="true">Verified</option>
						<option value="false">Unverified</option>
					</select>
				</div>
			</div>

			{/* Table */}
			{loading ? (
				<div className="flex items-center justify-center py-20">
					<div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
				</div>
			) : customers.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-20 text-slate-400">
					<Users className="w-16 h-16 mb-4 text-slate-300" />
					<p className="text-lg font-semibold text-slate-500">
						No customers found
					</p>
					<p className="text-sm">Try adjusting your search or filters</p>
				</div>
			) : (
				<div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
									<th className="px-6 py-3">Name</th>
									<th className="px-6 py-3">Email</th>
									<th className="px-6 py-3">Merchant</th>
									<th className="px-6 py-3">Role</th>
									<th className="px-6 py-3">Status</th>
									<th className="px-6 py-3 text-right">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{customers.map((c) => {
									const state = resend[c.id] ?? "idle";
									return (
										<tr
											key={c.id}
											className="hover:bg-slate-50/50 transition-colors"
										>
											<td className="px-6 py-3.5">
												<span className="font-medium text-slate-700">
													{c.name}
												</span>
												{c.phone && (
													<span className="block text-xs text-slate-400">
														{c.phone}
													</span>
												)}
											</td>
											<td className="px-6 py-3.5">
												<span className="text-slate-500 text-xs">
													{c.email}
												</span>
											</td>
											<td className="px-6 py-3.5">
												{c.merchantId ? (
													<Link
														to={`/merchants/${c.merchantId}`}
														className="flex items-center gap-1.5 text-slate-500 hover:text-primary transition-colors text-xs"
													>
														<Store className="w-3 h-3" />
														{c.merchantName ?? `#${c.merchantId}`}
													</Link>
												) : (
													<span className="text-xs text-slate-300">–</span>
												)}
											</td>
											<td className="px-6 py-3.5">
												<span className="inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500">
													{c.role}
												</span>
											</td>
											<td className="px-6 py-3.5">
												{c.verified ? (
													<span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success">
														<Check className="w-3 h-3" /> Verified
													</span>
												) : (
													<span className="text-[11px] font-semibold text-slate-400">
														Unverified
													</span>
												)}
											</td>
											<td className="px-6 py-3.5 text-right">
												{c.verified ? (
													<span className="text-xs text-slate-300">–</span>
												) : state === "sent" ? (
													<span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success">
														<Check className="w-3.5 h-3.5" /> Sent
													</span>
												) : state === "error" ? (
													<button
														type="button"
														onClick={() => handleResend(c.id)}
														className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-danger hover:underline"
													>
														<X className="w-3.5 h-3.5" /> Failed — retry
													</button>
												) : (
													<button
														type="button"
														onClick={() => handleResend(c.id)}
														disabled={state === "sending"}
														className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50 disabled:pointer-events-none"
													>
														{state === "sending" ? (
															<>
																<div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
																Sending…
															</>
														) : (
															<>
																<Send className="w-3.5 h-3.5" />
																Resend welcome
															</>
														)}
													</button>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
							<p className="text-xs text-slate-400">
								Page {page} of {totalPages}
							</p>
							<div className="flex items-center gap-1">
								<button
									type="button"
									onClick={() => setPage((p) => Math.max(1, p - 1))}
									disabled={page === 1}
									className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
								>
									<ChevronLeft className="w-4 h-4" />
								</button>
								{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
									const start = Math.max(1, Math.min(page - 2, totalPages - 4));
									const p = start + i;
									if (p > totalPages) return null;
									return (
										<button
											key={p}
											type="button"
											onClick={() => setPage(p)}
											className={`flex items-center justify-center w-8 h-8 rounded-lg border text-sm font-medium transition-colors ${
												p === page
													? "bg-primary text-white border-primary"
													: "border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary"
											}`}
										>
											{p}
										</button>
									);
								})}
								<button
									type="button"
									onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
									disabled={page === totalPages}
									className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
								>
									<ChevronRight className="w-4 h-4" />
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Customers;
